<template>
  <div>
    <SideBar v-if="sidebarVisible" />

    <!-- Listed routes keep the page while only the query changes (tabs, filters,
         `?setId=`), but a new record id or account mounts a fresh page, so record
         pages need no id watchers or per-record resets. Keyed on params, not the
         route name, so tab-style child routes (member/:id/notes -> member/:id/hr)
         keep their shell. -->
    <!-- An app-shell route (meta.appShell) gets one v-main and one top bar here, above its
         page, so the bar and the profile menu stay mounted while the page below changes.
         A shell branch with child pages (administration) is left unkeyed: Vue reuses its
         wrapper across the branch, and the wrapper renders its pages through KeyedRouterView,
         which keys each page with the usual page key (account included, so a switch still
         remounts it). A new shell branch must do the same, or a new id reuses the old page. -->
    <v-main v-if="appShellRecord" class="app-shell">
      <!-- client users get their own header; v-show: kept mounted on a page that draws its own
           bar, so it is not rebuilt on the way back -->
      <AppTopPanelClient v-if="isClient" />
      <AppTopBar v-else v-show="showTopBar" />
      <router-view v-slot="{ Component, route }">
        <component :is="Component" :key="isShellBranch ? undefined : getPageKey(route)" />
      </router-view>
    </v-main>
    <KeyedRouterView v-else />
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';

import { LayoutEnum } from '@/bundles/App/enums';
import { getPageKey } from '@/bundles/App/helpers/pageKey';
import { useAppShell } from '@/bundles/App/composables/useAppTopBar';
import { AccountLayoutMixin } from '@/bundles/App/mixins/accountMixin';
import { useUserRoles } from '@/bundles/App/composables/useUserRoles';
import { UserAccountsMixin } from '@/bundles/App/mixins/userAccountsMixin';
import { markSessionChanged } from '@/bundles/Auth/helpers/sessionChange';

import AppTopBar from '@/bundles/App/components/layout/AppTopBar.vue';
import AppTopPanelClient from '@/bundles/App/components/AppTopPanelClient.vue';
import KeyedRouterView from '@/bundles/App/components/layout/KeyedRouterView.vue';
import SideBar from '@/bundles/App/components/SideBar.vue';

export default defineComponent({
  name: 'AccountLayout',

  components: { AppTopBar, AppTopPanelClient, KeyedRouterView, SideBar },
  mixins: [AccountLayoutMixin, UserAccountsMixin],

  setup () {
    const { isClient } = useUserRoles();
    const { appShellRecord } = useAppShell();

    return { isClient, appShellRecord };
  },

  computed: {
    // Nearest match wins, so a layout can be declared on a whole branch or on a single route.
    sidebarVisible (): boolean {
      const record = [...this.$route.matched].reverse().find(({ meta }) => meta.layout);

      return (record?.meta.layout ?? LayoutEnum.Default) !== LayoutEnum.Blank;
    },

    // a shell branch (administration) renders its pages through its own router-view
    isShellBranch (): boolean {
      return !!this.appShellRecord?.children.length;
    },

    // a page that draws its own filter panel brings its own bar
    showTopBar (): boolean {
      return !this.$route.meta.useFilterPanel;
    },
  },

  watch: {
    '$route.params.accountId': {
      handler (newVal, prevVal) {
        if (newVal === prevVal) {
          return;
        }

        this.checkAccountAccess(newVal);
      },
      immediate: true
    },
    // Vue Router 4 resolves the initial route before mount, so the watcher above can run
    // while the user (and therefore `accounts`) is still being fetched. Re-check once it lands.
    accounts () {
      this.checkAccountAccess(this.$route.params.accountId);
    }
  },

  methods: {
    getPageKey,

    checkAccountAccess (accountId) {
      if (isNaN(accountId) || !this.accounts.length) {
        return;
      }

      const isAllowReLogin = this.accounts.some(account => account.id === +accountId);

      if (!isAllowReLogin) {
        this.$router.push(this.getAccountRoute({
          name: 'no-access',
          query: { id: accountId }
        }));
        return;
      }

      if (this.activeAccount?.id !== +accountId) {
        // the splash replaces the page before it mounts: it would load the other account's data with the
        // current token for the second the sign-in takes, and the reload drops it anyway
        this.toggleScreen(true);
        this.reLogin(accountId);
      }
    },
    async reLogin (id) {
      try {
        await this.accountLogin({ account: +id });
        this.resetFilters();

        const account = this.accounts.find(item => item.id === +id);
        if (account) {
          await this.activateAccount(account);
        }

        if (this.isMultipleAccountAuth) {
          this.clearTemporary();
        }

        this.toggleScreen(true);
        markSessionChanged();
        // the URL already names this account; reload it so the app boots with the account's settings
        window.location.reload();
      } catch (error) {
        const notification = { ...error };
        this.addNotification(notification);
        this.toggleScreen(false);
      }
    }
  }
});
</script>
